/**
 * OrganizationSettingsPage - organization configuration
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@stores/authStore';
import { organizationsApi } from '@services/api';
import { ContactImporter } from '@components/contacts';
import type { ContactBulkImportResponse } from '@services/api/contacts';

export default function OrganizationSettingsPage() {
  const { t } = useTranslation();
  const { currentOrganization, setCurrentOrganization } = useAuthStore();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [website, setWebsite] = useState('');
  const [defaultBoardView, setDefaultBoardView] = useState('kanban');
  const [allowPublicBoards, setAllowPublicBoards] = useState(false);
  const [requireTwoFactor, setRequireTwoFactor] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (currentOrganization) {
      setName(currentOrganization.name);
      setSlug(currentOrganization.slug);
      setDescription(currentOrganization.description ?? '');
      setLogoUrl(currentOrganization.logoUrl ?? '');
      setWebsite(currentOrganization.website ?? '');
      setDefaultBoardView(currentOrganization.settings?.defaultBoardView ?? 'kanban');
      setAllowPublicBoards(currentOrganization.settings?.allowPublicBoards ?? false);
      setRequireTwoFactor(currentOrganization.settings?.requireTwoFactor ?? false);
    }
  }, [currentOrganization]);

  const handleImportComplete = useCallback((result: ContactBulkImportResponse) => {
    setImportSuccess(
      `Successfully imported ${result.imported} contacts${result.skipped > 0 ? `, ${result.skipped} skipped` : ''}${result.failed > 0 ? `, ${result.failed} failed` : ''}.`
    );
    // Clear the success message after 5 seconds
    setTimeout(() => setImportSuccess(null), 5000);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization) return;

    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const updateData: Parameters<typeof organizationsApi.update>[1] = {
        name,
        slug,
        settings: {
          defaultBoardView: defaultBoardView as 'kanban' | 'list' | 'timeline' | 'calendar',
          allowPublicBoards,
          requireTwoFactor,
        },
      };
      if (description) {
        updateData.description = description;
      }
      if (logoUrl) {
        updateData.logoUrl = logoUrl;
      }
      if (website) {
        updateData.website = website;
      }
      const updatedOrg = await organizationsApi.update(currentOrganization.id, updateData);

      setCurrentOrganization(updatedOrg);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update organization');
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentOrganization) {
    return (
      <div className="p-6">
        <p className="text-neutral-500">{t('settings.noOrganization')}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <div>
        <h2 className="text-lg font-medium text-neutral-900 dark:text-white">
          {t('settings.organization')}
        </h2>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {t('settings.organizationDescription')}
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <p className="text-sm text-green-600 dark:text-green-400">
            {t('settings.organizationUpdated')}
          </p>
        </div>
      )}

      {importSuccess && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <p className="text-sm text-green-600 dark:text-green-400">{importSuccess}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
          <h3 className="text-base font-medium text-neutral-900 dark:text-white mb-4">
            {t('settings.basicInfo')}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="orgName"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('settings.organizationName')}
              </label>
              <input
                type="text"
                id="orgName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full rounded-md border border-neutral-300 bg-white text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
                required
              />
            </div>

            <div>
              <label
                htmlFor="orgSlug"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('settings.organizationSlug')}
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-neutral-300 bg-neutral-50 text-neutral-500 dark:border-neutral-600 dark:bg-neutral-600 dark:text-neutral-400 sm:text-sm">
                  app.neokanban.com/
                </span>
                <input
                  type="text"
                  id="orgSlug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  className="block w-full flex-1 rounded-none rounded-r-md border border-neutral-300 bg-white text-neutral-900 focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
                  required
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label
                htmlFor="orgDescription"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('settings.description')}
              </label>
              <textarea
                id="orgDescription"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-md border border-neutral-300 bg-white text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="logoUrl"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('settings.logoUrl')}
              </label>
              <input
                type="url"
                id="logoUrl"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
                className="mt-1 block w-full rounded-md border border-neutral-300 bg-white text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
              />
              {logoUrl && (
                <div className="mt-2">
                  <img
                    src={logoUrl}
                    alt="Organization logo"
                    className="h-12 w-12 object-contain rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="website"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('settings.website')}
              </label>
              <input
                type="url"
                id="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://example.com"
                className="mt-1 block w-full rounded-md border border-neutral-300 bg-white text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
              />
            </div>
          </div>
        </div>

        {/* Board Settings */}
        <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
          <h3 className="text-base font-medium text-neutral-900 dark:text-white mb-4">
            {t('settings.boardSettings')}
          </h3>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="defaultBoardView"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('settings.defaultBoardView')}
              </label>
              <select
                id="defaultBoardView"
                value={defaultBoardView}
                onChange={(e) => setDefaultBoardView(e.target.value)}
                className="mt-1 block w-full max-w-xs rounded-md border border-neutral-300 bg-white text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
              >
                <option value="kanban">{t('settings.viewKanban')}</option>
                <option value="list">{t('settings.viewList')}</option>
                <option value="timeline">{t('settings.viewTimeline')}</option>
                <option value="calendar">{t('settings.viewCalendar')}</option>
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="allowPublicBoards"
                checked={allowPublicBoards}
                onChange={(e) => setAllowPublicBoards(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
              />
              <label
                htmlFor="allowPublicBoards"
                className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300"
              >
                {t('settings.allowPublicBoards')}
              </label>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
          <h3 className="text-base font-medium text-neutral-900 dark:text-white mb-4">
            {t('settings.securitySettings')}
          </h3>

          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="requireTwoFactor"
                checked={requireTwoFactor}
                onChange={(e) => setRequireTwoFactor(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
              />
              <label
                htmlFor="requireTwoFactor"
                className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300"
              >
                {t('settings.requireTwoFactor')}
              </label>
            </div>
            <p className="text-xs text-neutral-500 ml-6">
              {t('settings.requireTwoFactorDescription')}
            </p>
          </div>
        </div>

        {/* Submit */}
        <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6 flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {isSaving ? t('common.saving') : t('common.saveChanges')}
          </button>
        </div>
      </form>

      {/* Contact Directory Import */}
      <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
        <h3 className="text-base font-medium text-neutral-900 dark:text-white mb-2">
          {t('settings.contactDirectory', 'Contact Directory')}
        </h3>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
          {t(
            'settings.contactDirectoryDescription',
            'Import contacts from a CSV file to build your organization directory. These contacts can be used when adding meeting attendees.'
          )}
        </p>
        <ContactImporter
          organizationId={currentOrganization.id}
          onImportComplete={handleImportComplete}
        />
      </div>
    </div>
  );
}
